
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Button, Input, CustomSelect } from '../components/UI';
import { 
  Check, ArrowRight, Building, Mail, Zap, Archive, 
  Shield, Lock, FileText, Printer, ListOrdered, ChevronUp, ChevronDown
} from '../components/Icons';
import { BrandLogo } from '../components/BrandLogo';
import { ChannelType } from '../types';
import { useAuth } from '../contexts/AuthContext';

type Step = 'company' | 'intake' | 'distribution' | 'priority' | 'finalizing';

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  
  // Persistence key
  const ONBOARDING_STATE_KEY = 'vmi_onboarding_progress';

  // State
  const [currentStep, setCurrentStep] = useState<Step>('company');
  const [configuredIntake, setConfiguredIntake] = useState<string[]>([]);
  const [configuredDist, setConfiguredDist] = useState<string[]>([]);
  const [priorityOrder, setPriorityOrder] = useState<string[]>([]);
  const [companyName, setCompanyName] = useState('');
  const [sector, setSector] = useState('Manufacturing');
  const [shouldSkipProfile, setShouldSkipProfile] = useState(false);
  
  // Finalizing animation state
  const [finalizingStatus, setFinalizingStatus] = useState('Initializing Environment');

  // Load progress
  useEffect(() => {
    const saved = localStorage.getItem(ONBOARDING_STATE_KEY);
    const queryParams = new URLSearchParams(location.search);
    const skipProfileParam = queryParams.get('skipProfile') === 'true';
    let isSkippingProfile = skipProfileParam;

    if (saved) {
      const parsed = JSON.parse(saved);
      setConfiguredIntake(parsed.intake || []);
      setConfiguredDist(parsed.dist || []);
      setPriorityOrder(parsed.priority || []);
      setCompanyName(parsed.company || '');
      setSector(parsed.sector || 'Manufacturing');
      
      // Check for saved skipProfile state or if current navigation forces it
      if (parsed.skipProfile || skipProfileParam) {
         setShouldSkipProfile(true);
         isSkippingProfile = true;
         // Persist if came from URL and not yet saved
         if (skipProfileParam && !parsed.skipProfile) {
             saveProgress({ skipProfile: true });
         }
      }
    } else if (skipProfileParam) {
        setShouldSkipProfile(true);
        saveProgress({ skipProfile: true });
        isSkippingProfile = true;
    }
    
    // Check if we just returned from a configuration page
    const step = queryParams.get('step') as Step;
    const finishedId = queryParams.get('finished');
    const category = queryParams.get('category');
    
    if (step) {
      setCurrentStep(step);
    } else if (isSkippingProfile && currentStep === 'company') {
      // Default to intake if skipping profile and no specific step is requested
      setCurrentStep('intake');
    }
    
    if (finishedId && category) {
      if (category === 'intake') {
        setConfiguredIntake(prev => prev.includes(finishedId) ? prev : [...prev, finishedId]);
        saveProgress({ intake: configuredIntake.includes(finishedId) ? configuredIntake : [...configuredIntake, finishedId] });
      } else {
        setConfiguredDist(prev => prev.includes(finishedId) ? prev : [...prev, finishedId]);
        saveProgress({ dist: configuredDist.includes(finishedId) ? configuredDist : [...configuredDist, finishedId] });
      }
    }
  }, [location]);

  // Handle Finalization redirect & Animation
  useEffect(() => {
    if (currentStep === 'finalizing') {
      // Animation sequence
      const statusSteps = [
        { msg: 'Verifying Domain Authority', time: 1000 },
        { msg: 'Provisioning Secure Tenant', time: 2500 },
        { msg: 'Activating Gateways', time: 3800 },
        { msg: 'Setup Complete', time: 4500 }
      ];

      statusSteps.forEach(({ msg, time }) => {
        setTimeout(() => setFinalizingStatus(msg), time);
      });

      const timer = setTimeout(() => {
        localStorage.removeItem(ONBOARDING_STATE_KEY);
        if (!isAuthenticated) {
            login('onboarding-user@company.com');
        }
        navigate('/dashboard?new=true');
      }, 4800);
      return () => clearTimeout(timer);
    }
  }, [currentStep, navigate, isAuthenticated, login]);

  // Save progress
  const saveProgress = (updates: any) => {
    const current = JSON.parse(localStorage.getItem(ONBOARDING_STATE_KEY) || '{}');
    localStorage.setItem(ONBOARDING_STATE_KEY, JSON.stringify({ ...current, ...updates }));
  };

  const openInternalConfig = (path: string, type: string, category: 'intake' | 'dist') => {
    // Save current step so we can return to it
    saveProgress({ 
        company: companyName, 
        sector: sector, 
        intake: configuredIntake, 
        dist: configuredDist,
        priority: priorityOrder,
        skipProfile: shouldSkipProfile // Persist skip state
    });
    
    // Ensure auth for internal pages
    if (!isAuthenticated) {
        login('onboarding-user@company.com'); 
    }

    // Navigate to the real config page with shell hidden
    navigate(`${path}?shell=false&onboarding=true&type=${type}&category=${category}&step=${currentStep}`);
  };

  const handleSkip = () => {
    // We preserve local storage so progress is saved if they return via "Setup All" later
    if (!isAuthenticated) {
        login('onboarding-user@company.com');
    }
    navigate('/dashboard');
  };

  const movePriority = (index: number, direction: 'up' | 'down') => {
    const newOrder = [...priorityOrder];
    if (direction === 'up' && index > 0) {
      [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
    } else if (direction === 'down' && index < newOrder.length - 1) {
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    }
    setPriorityOrder(newOrder);
    saveProgress({ priority: newOrder });
  };

  const allSteps = [
    { id: 'company', label: 'Profile' },
    { id: 'intake', label: 'Intake' },
    { id: 'distribution', label: 'Channels' },
    { id: 'priority', label: 'Priority' },
    { id: 'finalizing', label: 'Finish' }
  ];

  // Filter out Profile step if skipping profile
  const steps = shouldSkipProfile ? allSteps.filter(s => s.id !== 'company') : allSteps;

  const renderProgress = () => (
    <div className="flex items-center justify-center gap-6 mb-16 animate-in fade-in duration-700">
      {steps.map((step, idx) => {
        if (step.id === 'finalizing') return null; // Don't show finalizing step in progress bar
        
        const isActive = currentStep === step.id;
        const isCompleted = steps.findIndex(s => s.id === currentStep) > idx;
        
        return (
          <React.Fragment key={step.id}>
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold border transition-all duration-300 ${
                isActive 
                  ? 'bg-[#474D22] border-[#474D22] text-white scale-110 shadow-lg' 
                  : isCompleted
                    ? 'bg-green-600 border-green-600 text-white'
                    : 'bg-white dark:bg-[#111111] border-gray-200 dark:border-gray-800 text-gray-400'
              }`}>
                {isCompleted ? <Check size={14} strokeWidth={3} /> : idx + 1}
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-widest transition-colors duration-300 ${isActive ? 'text-[#474D22] dark:text-white' : 'text-gray-400'}`}>
                {step.label}
              </span>
            </div>
            {idx < steps.length - 2 && (
              <div className={`w-12 h-px transition-colors duration-500 ${isCompleted ? 'bg-green-600' : 'bg-gray-200 dark:bg-gray-800'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  const renderCompanyStep = () => (
    <div className="space-y-10 animate-in slide-in-from-bottom-4 fade-in duration-500 max-w-xl mx-auto w-full">
      <div className="text-center space-y-3">
        <h2 className="text-3xl font-light text-[#161616] dark:text-white tracking-tight">Organization Profile</h2>
        <p className="text-sm text-gray-500 font-medium">Establish your tenant identity.</p>
      </div>

      <Card className="p-10 space-y-8 bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#262626] shadow-xl rounded-[4px]">
        <div className="space-y-2">
          <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">Legal Entity Name</label>
          <div className="relative group">
             <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#474D22] transition-colors"><Building size={18} /></div>
             <Input 
               value={companyName} 
               onChange={(e) => setCompanyName(e.target.value)} 
               placeholder="e.g. Acme Logistics AG" 
               className="pl-12 h-14 text-lg bg-gray-50 dark:bg-[#1A1A1A] border-transparent focus:bg-white dark:focus:bg-[#111111]"
             />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <CustomSelect 
            label="Industry Sector" 
            value={sector} 
            options={['Manufacturing', 'Retail', 'Financial Services', 'Logistics', 'Public Sector']} 
            onSelect={setSector}
            className="h-14"
          />
          <CustomSelect 
            label="Data Residency" 
            value="Switzerland (CH)" 
            options={['Switzerland (CH)', 'Germany (DE)', 'Austria (AT)', 'Global']} 
            onSelect={()=>{}} 
            className="h-14"
          />
        </div>

        <div className="pt-6">
           <Button 
            disabled={!companyName} 
            onClick={() => {
                saveProgress({ company: companyName, sector: sector });
                setCurrentStep('intake');
            }}
            className="w-full h-14 shadow-lg text-sm tracking-widest font-bold !bg-[#474D22] hover:!bg-[#3a3f1c]"
           >
             CONTINUE <ArrowRight size={16} className="ml-3" />
           </Button>
        </div>
      </Card>
    </div>
  );

  const renderIntakeStep = () => (
    <div className="space-y-10 animate-in slide-in-from-right-8 fade-in duration-500 w-full max-w-5xl mx-auto">
      <div className="text-center space-y-3">
        <h2 className="text-3xl font-light text-[#161616] dark:text-white tracking-tight">Data Ingestion</h2>
        <p className="text-sm text-gray-500 font-medium">Configure how your ERP transmits data to the gateway.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { id: 'sftp', label: 'Secure SFTP', icon: Archive, desc: 'High-volume batch processing via secure shell transfer.', path: '/settings/inbound/sftp' },
          { id: 'email', label: 'eMail Gateway', icon: Mail, desc: 'Process attachments from authorized sender domains.', path: '/settings/inbound/email' },
          { id: 'api', label: 'REST API', icon: Zap, desc: 'Real-time JSON/XML integration for modern stacks.', path: '/settings/inbound/api' }
        ].map((item, i) => (
          <button 
            key={item.id}
            onClick={() => openInternalConfig(item.path, item.id, 'intake')}
            className={`relative group p-10 border flex flex-col items-center text-center gap-6 transition-all duration-300 rounded-[6px] hover:-translate-y-1 ${
              configuredIntake.includes(item.id)
                ? 'border-[#474D22] bg-[#474D22]/5 dark:bg-[#474D22]/10'
                : 'border-gray-200 dark:border-[#262626] bg-white dark:bg-[#111111] hover:border-[#474D22] hover:shadow-lg'
            }`}
            style={{ animationDelay: `${i * 100}ms` }}
          >
            <div className={`transition-colors duration-300 ${
              configuredIntake.includes(item.id) 
                ? 'text-[#474D22]' 
                : 'text-gray-300 group-hover:text-[#474D22]'
            }`}>
              <item.icon size={42} strokeWidth={1} />
            </div>
            
            <div className="space-y-2">
              <p className="text-base font-bold uppercase tracking-wider text-[#161616] dark:text-white">{item.label}</p>
              <p className="text-xs text-gray-500 leading-relaxed max-w-[240px] mx-auto">{item.desc}</p>
            </div>

            <div className={`mt-auto pt-6 w-full border-t border-dashed ${configuredIntake.includes(item.id) ? 'border-[#474D22]/30' : 'border-gray-200 dark:border-[#262626]'}`}>
              {configuredIntake.includes(item.id) ? (
                <div className="text-[#474D22] font-bold text-[10px] uppercase tracking-widest flex items-center justify-center gap-2">
                   <div className="bg-[#474D22] text-white rounded-full p-0.5"><Check size={10} strokeWidth={4} /></div>
                   Configured
                </div>
              ) : (
                 <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest group-hover:text-[#474D22] transition-colors flex items-center justify-center gap-2">
                    Configure <ArrowRight size={12} />
                 </div>
              )}
            </div>
          </button>
        ))}
      </div>

      <div className="flex justify-center gap-4 pt-8">
        <Button 
            variant="ghost" 
            onClick={() => shouldSkipProfile ? navigate('/dashboard') : setCurrentStep('company')} 
            className="px-12 h-12 text-gray-500"
        >
            {shouldSkipProfile ? 'Cancel' : 'Back'}
        </Button>
        <Button 
          disabled={configuredIntake.length === 0} 
          onClick={() => {
              saveProgress({ intake: configuredIntake });
              setCurrentStep('distribution');
          }}
          className="px-12 h-12 shadow-xl !bg-[#474D22] text-white hover:!bg-[#3a3f1c]"
        >
          Next Step
        </Button>
      </div>
    </div>
  );

  const renderDistStep = () => (
    <div className="space-y-10 animate-in slide-in-from-right-8 fade-in duration-500 w-full max-w-6xl mx-auto">
      <div className="text-center space-y-3">
        <h2 className="text-3xl font-light text-[#161616] dark:text-white tracking-tight">Distribution Channels</h2>
        <p className="text-sm text-gray-500 font-medium">Select output channels for your customer documents.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {[
          { id: 'ebill', label: 'eBill', type: ChannelType.EBILL, icon: FileText, color: '#474D22', desc: 'Swiss Banking Network', path: '/settings/channels/ebill' },
          { id: 'edi', label: 'EDI', type: ChannelType.EDI, icon: Zap, color: '#717C26', desc: 'B2B Integration', path: '/settings/channels/edi' },
          { id: 'email', label: 'eMail', type: ChannelType.EMAIL, icon: Mail, color: '#2D5A27', desc: 'PDF Delivery', path: '/settings/channels/email' },
          { id: 'print', label: 'Print', type: ChannelType.PRINT, icon: Printer, color: '#6A7328', desc: 'Postal Service', path: '/settings/channels/print' }
        ].map((item, i) => (
          <button 
            key={item.id}
            onClick={() => openInternalConfig(item.path, item.id, 'dist')}
            className={`relative group p-8 border flex flex-col items-center gap-6 transition-all duration-300 rounded-[6px] hover:-translate-y-1 ${
              configuredDist.includes(item.id)
                ? 'border-[#474D22] bg-[#474D22]/5 dark:bg-[#474D22]/10'
                : 'border-gray-200 dark:border-[#262626] bg-white dark:bg-[#111111] hover:border-[#474D22] hover:shadow-lg'
            }`}
            style={{ animationDelay: `${i * 100}ms` }}
          >
            {/* CLEAN ICON WITHOUT BACKGROUND BOX */}
            <div className={`transition-all duration-300 ${configuredDist.includes(item.id) ? 'scale-110 drop-shadow-md' : 'grayscale opacity-50 group-hover:grayscale-0 group-hover:opacity-100'}`}>
               <item.icon size={56} style={{ color: configuredDist.includes(item.id) ? item.color : 'inherit' }} strokeWidth={1} />
            </div>
            
            <div className="space-y-1 text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-[#161616] dark:text-white">{item.label}</p>
              <p className="text-[10px] text-gray-400 font-medium uppercase tracking-tight">{item.desc}</p>
            </div>

            <div className="absolute top-4 right-4">
              {configuredDist.includes(item.id) ? (
                <div className="w-5 h-5 bg-[#474D22] rounded-full flex items-center justify-center text-white shadow-sm">
                   <Check size={10} strokeWidth={4} />
                </div>
              ) : (
                 <div className="w-5 h-5 rounded-full border border-gray-200 dark:border-gray-700 flex items-center justify-center group-hover:border-[#474D22] group-hover:text-[#474D22] transition-colors">
                    <ArrowRight size={10} />
                 </div>
              )}
            </div>
          </button>
        ))}
      </div>

      <div className="flex justify-center gap-4 pt-8">
        <Button variant="ghost" onClick={() => setCurrentStep('intake')} className="px-12 h-12 text-gray-500">Back</Button>
        <Button 
          disabled={configuredDist.length === 0} 
          onClick={() => {
              saveProgress({ dist: configuredDist });
              // Initialize priority order with current selection (preserving order if exists, adding new ones)
              setPriorityOrder(prev => {
                 const existing = prev.filter(p => configuredDist.includes(p));
                 const newItems = configuredDist.filter(d => !prev.includes(d));
                 return [...existing, ...newItems];
              });
              setCurrentStep('priority');
          }}
          className="px-12 h-12 shadow-xl !bg-[#474D22] hover:!bg-[#3a3f1c] text-white"
        >
          Next Step
        </Button>
      </div>
    </div>
  );

  const renderPriorityStep = () => {
    const allChannels = [
      { id: 'ebill', label: 'eBill', type: ChannelType.EBILL },
      { id: 'edi', label: 'EDI', type: ChannelType.EDI },
      { id: 'email', label: 'eMail', type: ChannelType.EMAIL },
      { id: 'print', label: 'Print', type: ChannelType.PRINT }
    ];

    // Separate active channels (sorted by priorityOrder) and inactive ones
    const activeChannels = priorityOrder.map(id => allChannels.find(c => c.id === id)).filter(Boolean) as typeof allChannels;
    const inactiveChannels = allChannels.filter(c => !priorityOrder.includes(c.id));

    return (
      <div className="space-y-10 animate-in slide-in-from-right-8 fade-in duration-500 max-w-2xl mx-auto w-full">
        <div className="text-center space-y-3">
          <h2 className="text-3xl font-light text-[#161616] dark:text-white tracking-tight">Channel Priority</h2>
          <p className="text-sm text-gray-500 font-medium">Define the fallback order for your outbound documents.</p>
        </div>

        <Card className="bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#262626] p-0 overflow-hidden shadow-lg">
           <div className="p-6 bg-[#f9fafb] dark:bg-[#1A1A1A] border-b border-gray-200 dark:border-[#333] flex items-center justify-between">
              <div className="flex items-center gap-2">
                 <ListOrdered size={18} className="text-[#474D22]" />
                 <span className="text-xs font-bold uppercase tracking-widest text-[#161616] dark:text-white">Processing Order</span>
              </div>
              {priorityOrder.length === 1 && (
                 <span className="text-[10px] font-bold uppercase tracking-widest text-[#474D22] bg-[#474D22]/10 px-2 py-1 rounded">Single Channel</span>
              )}
           </div>

           <div className="divide-y divide-gray-100 dark:divide-[#262626]">
              {/* Active Channels */}
              {activeChannels.map((channel, index) => (
                 <div key={channel.id} className="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-[#161616] transition-colors group">
                    <div className="flex items-center gap-4">
                       <span className="w-6 h-6 rounded-full bg-[#474D22] text-white flex items-center justify-center text-[10px] font-bold shadow-sm">
                          {index + 1}
                       </span>
                       <div className="flex items-center gap-3">
                          <BrandLogo type={channel.type} size={32} />
                          <span className="text-sm font-bold text-[#161616] dark:text-white uppercase tracking-wider">{channel.label}</span>
                       </div>
                    </div>
                    
                    {activeChannels.length > 1 && (
                       <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button 
                             onClick={() => movePriority(index, 'up')}
                             disabled={index === 0}
                             className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-[#333] transition-colors ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500'}`}
                          >
                             <ChevronUp size={16} />
                          </button>
                          <button 
                             onClick={() => movePriority(index, 'down')}
                             disabled={index === activeChannels.length - 1}
                             className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-[#333] transition-colors ${index === activeChannels.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500'}`}
                          >
                             <ChevronDown size={16} />
                          </button>
                       </div>
                    )}
                 </div>
              ))}

              {/* Inactive Channels */}
              {inactiveChannels.map((channel) => (
                 <div key={channel.id} className="p-4 flex items-center gap-4 bg-gray-50/50 dark:bg-[#0A0A0A]/50 opacity-60">
                    <span className="w-6 h-6 flex items-center justify-center text-[10px] font-bold text-gray-400">
                       -
                    </span>
                    <div className="flex items-center gap-3 grayscale">
                       <BrandLogo type={channel.type} size={32} />
                       <span className="text-sm font-bold text-gray-400 uppercase tracking-wider">{channel.label}</span>
                    </div>
                    <div className="ml-auto text-[9px] font-bold uppercase tracking-widest text-gray-400 border border-gray-200 px-2 py-0.5 rounded">
                       Disabled
                    </div>
                 </div>
              ))}
           </div>
        </Card>

        <div className="flex justify-center gap-4 pt-4">
          <Button variant="ghost" onClick={() => setCurrentStep('distribution')} className="px-12 h-12 text-gray-500">Back</Button>
          <Button 
            onClick={() => {
                saveProgress({ priority: priorityOrder });
                setCurrentStep('finalizing');
            }}
            className="px-12 h-12 shadow-xl !bg-[#474D22] hover:!bg-[#3a3f1c] text-white"
          >
            Complete Setup
          </Button>
        </div>
      </div>
    );
  };

  const renderFinalizing = () => {
    return (
      <div className="fixed inset-0 z-50 bg-[#F9FAFB] dark:bg-[#050505] flex flex-col items-center justify-center animate-in fade-in duration-1000">
        <div className="relative">
           {/* Simple Clean Loader */}
           <div className="w-24 h-24 rounded-full border-4 border-gray-200 dark:border-[#333] border-t-[#474D22] animate-spin"></div>
           <div className="absolute inset-0 flex items-center justify-center">
              <BrandLogo type="main" size={40} />
           </div>
        </div>

        <div className="mt-12 text-center space-y-2">
           <h2 className="text-lg font-bold text-[#161616] dark:text-white uppercase tracking-widest animate-pulse">
             {finalizingStatus}
           </h2>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen bg-[#F9FAFB] dark:bg-[#050505] flex flex-col items-center transition-colors duration-500 ${currentStep === 'finalizing' ? 'overflow-hidden' : 'p-6 lg:p-12'}`}>
      
      {currentStep !== 'finalizing' && (
        <div className="w-full max-w-7xl animate-in fade-in slide-in-from-top-4 duration-700">
          <div className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-3">
              <BrandLogo type="main" size={32} />
              <span className="text-sm font-bold tracking-widest text-[#161616] dark:text-white uppercase">VMI Hub</span>
            </div>
            
            <div className="flex items-center gap-6">
               <button 
                 onClick={handleSkip}
                 className="text-[10px] font-bold text-gray-400 hover:text-[#474D22] dark:hover:text-white uppercase tracking-widest transition-colors hover:underline underline-offset-4"
               >
                 Skip Setup
               </button>
               <div className="h-4 w-px bg-gray-300 dark:bg-gray-700 hidden sm:block"></div>
               <div className="hidden sm:flex items-center gap-6 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  <span className="flex items-center gap-1.5"><Shield size={12}/> Secure Config</span>
                  <span className="flex items-center gap-1.5"><Lock size={12}/> Encrypted</span>
               </div>
            </div>
          </div>

          {renderProgress()}

          <div className="mt-8 flex justify-center w-full">
            {currentStep === 'company' && renderCompanyStep()}
            {currentStep === 'intake' && renderIntakeStep()}
            {currentStep === 'distribution' && renderDistStep()}
            {currentStep === 'priority' && renderPriorityStep()}
          </div>
        </div>
      )}

      {currentStep === 'finalizing' && renderFinalizing()}
    </div>
  );
};

export default Onboarding;
